import {
  type FC, useCallback, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useModal } from '~/modal/useModal.hook';
import { useIsMobileScreenSelector } from '~/store/frontendState/deviceInfo/deviceInfo.selector';
import { openMapTool } from '~/store/frontendState/leftSidebar/leftSidebar.actionCreators';
import {
  frontendStateSearchHide, frontendStateSearchSetSearchText,
} from '~/store/frontendState/mapTools/search/search.actionCreators';
import { useSearchTextSelector } from '~/store/frontendState/mapTools/search/search.selectors';
import { placesToolSetSearchTerm } from '~/store/frontendState/places/places.actionCreators';
import {
  usePublicMapSettingsSearchBarAlwaysVisibleSelector, usePublicMapSettingsSearchFilterDefaultOption,
  usePublicMapSettingsSelector,
} from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.selectors';
import {
  mapSettingsSearchSetFilterBy, setMapSearchSettings,
} from '~/store/mapSettings/search/mapSettingsSearch.actionCreators';
import {
  useMapSettingsSearchFilterBySelector, useMapSettingsSearchIsStackingItemsAllowedSelector,
  useMapSettingsSearchShouldMatchExactWordsSelector,
} from '~/store/mapSettings/search/mapSettingsSearch.selectors';
import {
  mapSettingsSearchItemsAddItem, mapSettingsSearchItemsRemoveAllItems,
} from '~/store/mapSettings/searchItems/mapSettingsSearchItems.actionCreators';
import { useSearchItemsSelector } from '~/store/mapSettings/searchItems/mapSettingsSearchItems.selectors';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { useIsMapPresentationalSelector } from '~/store/selectors/useMapInfoSelectors';
import { useSpreadsheetIdsSelector } from '~/store/selectors/useSpreadsheetIdsSelector';
import { ALL_COLUMNS_ID } from '~/store/spreadsheetData/filtering/spreadsheetDataFiltering.constants';
import { MapSearchFilterBy } from '../filter/mapSearchFilterBy.enum';
import { MapSearchComponent } from './mapSearch.component';
import {
  areSearchItemsSame, type SearchItem,
} from './mapSearch.helpers';
import { useMapSearchSuggests } from './useMapSearchSuggests';

type MapSearchContainerProps = Readonly<{
  className?: string;
}>;

export const MapSearchContainer: FC<MapSearchContainerProps> = (props) => {
  const mapSettingsSearchFilterBy = useMapSettingsSearchFilterBySelector();
  const isMapPresentational = useIsMapPresentationalSelector();
  const publicMapSearchFilterDefaultOption = usePublicMapSettingsSearchFilterDefaultOption();
  const [filterBy, setFilterBy] = useState<MapSearchFilterBy | null>(() => isMapPresentational ? publicMapSearchFilterDefaultOption : mapSettingsSearchFilterBy);
  const shouldMatchExactWords = useMapSettingsSearchShouldMatchExactWordsSelector();
  const shouldStackItems = useMapSettingsSearchIsStackingItemsAllowedSelector();
  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector();
  const spreadsheetIds = useSpreadsheetIdsSelector();
  const searchText = useSearchTextSelector();
  const isMobileScreen = useIsMobileScreenSelector();
  const searchBarAlwaysVisible = usePublicMapSettingsSearchBarAlwaysVisibleSelector();
  const existingSearchItems = useSearchItemsSelector();
  const publicMapSettings = usePublicMapSettingsSelector();
  const dispatch = useDispatch();
  const { openModal: openSearchSettingsModal } = useModal('SearchSettings');

  const searchSuggestsParams = useMemo(() => ({
    query: searchText,
    spreadsheetIds,
    exact: shouldMatchExactWords,
    filterBy,
  }), [filterBy, searchText, shouldMatchExactWords, spreadsheetIds]);

  const { suggests, isLoading } = useMapSearchSuggests(clientId, mapId, searchSuggestsParams);

  const onSetMapSearchSettings = useCallback((settings: Parameters<typeof setMapSearchSettings>[0]) => {
    dispatch(setMapSearchSettings(settings));
  }, [dispatch]);

  const addSearchSuggest = useCallback((searchSuggest: SearchItem) => {
    dispatch(mapSettingsSearchItemsAddItem(searchSuggest));
  }, [dispatch]);

  const setSearchText = useCallback((newSearchText: string) => {
    dispatch(frontendStateSearchSetSearchText(newSearchText));
  }, [dispatch]);

  const closeSearchBar = useCallback(() => {
    dispatch(frontendStateSearchHide());
  }, [dispatch]);

  const onFilterByChange = useCallback((filterBy: MapSearchFilterBy) => {
    setFilterBy(filterBy);
    dispatch(mapSettingsSearchSetFilterBy(filterBy));
  }, [dispatch]);

  const handleCloseSearchBar = useMemo(() => {
    if (!isMapPresentational || !searchBarAlwaysVisible) {
      return closeSearchBar;
    }

    return undefined;
  }, [closeSearchBar, isMapPresentational, searchBarAlwaysVisible]);

  const onShowConfig = useCallback(() => {
    openSearchSettingsModal({
      onSave: v => onSetMapSearchSettings({
        selectedMatchingBehaviour: v.matchingBehaviour,
        shouldMatchExactWords: v.shouldMatchExactWords,
        shouldZoomOnMatches: v.shouldZoomOnMatches,
        shouldStackItems: v.matchesStackingAllowed,
      }),
    });
  }, [onSetMapSearchSettings, openSearchSettingsModal]);

  const onSearchSuggestSubmit = useCallback((searchSuggest: SearchItem) => {
    setSearchText('');

    if (filterBy === MapSearchFilterBy.PlacesAndPoi) {
      dispatch(placesToolSetSearchTerm(searchSuggest.text));
      dispatch(openMapTool(MapTool.Places));
      return;
    }

    const exactWords = shouldMatchExactWords && !searchSuggest.latLng;
    const includeAllGeolocated = filterBy && !searchSuggest.latLng && (!searchSuggest.columnId || searchSuggest.columnId === ALL_COLUMNS_ID)
      ? [MapSearchFilterBy.OnlyGeolocated, MapSearchFilterBy.UserDataAndGeolocated].includes(filterBy) : false;

    const newSearchSuggest = {
      ...searchSuggest,
      exactWords,
      includeAllGeolocated,
    };

    if (filterBy === MapSearchFilterBy.OnlyGeolocated) {
      delete newSearchSuggest.columnId;
      delete newSearchSuggest.columnName;
    }

    if (!shouldStackItems) {
      dispatch(mapSettingsSearchItemsRemoveAllItems());
      addSearchSuggest(newSearchSuggest);
    }
    else if (!existingSearchItems.find(item => areSearchItemsSame(item, newSearchSuggest))) {
      addSearchSuggest(newSearchSuggest);
    }
  }, [dispatch, addSearchSuggest, setSearchText, existingSearchItems, filterBy, shouldMatchExactWords, shouldStackItems]);

  const showFilterDropdown = isMapPresentational ? publicMapSettings.showSearchFilter : true;

  return (
    <MapSearchComponent
      {...props}
      onSetMapSearchText={setSearchText}
      searchText={searchText}
      filterBy={filterBy}
      onSetFilterBy={showFilterDropdown ? onFilterByChange : undefined}
      searchSuggests={suggests}
      areSearchSuggestsLoading={isLoading}
      onSearchSuggestSubmit={onSearchSuggestSubmit}
      onCloseSearchBar={handleCloseSearchBar}
      isMobileScreen={isMobileScreen}
      onShowConfig={isMapPresentational || filterBy === MapSearchFilterBy.PlacesAndPoi ? undefined : onShowConfig}
    />
  );
};
